import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, TextField } from '@mui/material'
import { IKoreanLeadingSchool, KoreanLeadingSchoolType } from 'Entities/koreanLeadingSchool'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { useKoreanLeadingSchools } from '../Hooks/useKoreanLeadingSchools'

const TYPE_OPTIONS: { value: KoreanLeadingSchoolType; label: string }[] = [
  { value: 'LEADING_SCHOOL', label: 'Образцовая школа' },
  { value: 'KOREAN_CULTURE_SPECIALIZED_SCHOOL', label: 'Школа углубленного изучения культуры Кореи' },
]

const isLeadingSchoolType = (type: string): type is KoreanLeadingSchoolType =>
  type === 'LEADING_SCHOOL' || type === 'KOREAN_CULTURE_SPECIALIZED_SCHOOL'

type Props = {
  open: boolean
  onClose: () => void
  school: IKoreanLeadingSchool
}

export const KoreanLeadingSchoolEditDialog = ({ open, onClose, school }: Props) => {
  const { updateLeadingSchoolMutation } = useKoreanLeadingSchools()

  const [form, setForm] = useState({
    type: 'LEADING_SCHOOL' as KoreanLeadingSchoolType,
    schoolNameUz: '',
    schoolNameRu: '',
    schoolNameKo: '',
  })

  useEffect(() => {
    setForm({
      type: isLeadingSchoolType(String(school.type)) ? (school.type as KoreanLeadingSchoolType) : 'LEADING_SCHOOL',
      schoolNameUz: school.schoolNameUz ?? '',
      schoolNameRu: school.schoolNameRu ?? '',
      schoolNameKo: school.schoolNameKo ?? '',
    })
  }, [school])

  const hasError = useMemo(
    () => !form.schoolNameUz.trim() || !form.schoolNameRu.trim() || !form.schoolNameKo.trim(),
    [form.schoolNameKo, form.schoolNameRu, form.schoolNameUz],
  )

  const handleChange = (field: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, [field]: event.target.value }))
  }

  const handleSubmit = () => {
    const prevType: KoreanLeadingSchoolType = isLeadingSchoolType(String(school.type))
      ? (school.type as KoreanLeadingSchoolType)
      : 'LEADING_SCHOOL'

    if (
      form.schoolNameUz === school.schoolNameUz &&
      form.schoolNameRu === school.schoolNameRu &&
      form.schoolNameKo === school.schoolNameKo &&
      form.type === prevType
    ) {
      return toast.warning('Ничего не изменилось!')
    }

    if (hasError) return

    updateLeadingSchoolMutation.mutate(
      {
        leadingSchoolId: school.leadingSchoolId,
        payload: {
          type: form.type,
          regionCode: school.regionCode,
          schoolNameUz: form.schoolNameUz.trim(),
          schoolNameRu: form.schoolNameRu.trim(),
          schoolNameKo: form.schoolNameKo.trim(),
        },
      },
      { onSuccess: onClose },
    )
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='md'>
      <DialogTitle>Редактировать образцовую школу</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} mt={0.5}>
          <Grid item xs={12}>
            <TextField select fullWidth label='Тип школы' value={form.type} onChange={handleChange('type')}>
              {TYPE_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField fullWidth label='Название (UZ)' value={form.schoolNameUz} onChange={handleChange('schoolNameUz')} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label='Название (RU)' value={form.schoolNameRu} onChange={handleChange('schoolNameRu')} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label='Название (KO)' value={form.schoolNameKo} onChange={handleChange('schoolNameKo')} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color='inherit'>
          Отмена
        </Button>
        <Button onClick={handleSubmit} variant='contained' disabled={hasError || updateLeadingSchoolMutation.isLoading}>
          Сохранить
        </Button>
      </DialogActions>
    </Dialog>
  )
}
